<!-- Quote editor — devOnly. Creates/edits quotes, sends them to clients via a public preview link.
     Tax rate is auto-detected from the job's Canadian province. -->
<script setup>
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { getAuthToken, company } from '@/stores/appConfig';
import { logAction } from '@/utils/logAction';

const route = useRoute();

const { hostname } = window.location;
const API_BASE_URL = (() => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000/api'
  }
  if (hostname === 'quotastic.ca') {
    return 'https://api.quotastic.ca/api'
  }
  return 'https://api-staging.quotastic.ca/api'
})()



// variables
//const taxRate = ref(taxRates[quote.jobProvince])
const taxRates = {
  ON: 13,
  BC: 12,
  AB: 5,
  SK: 11,
  MB: 12,
  QC: 14.975,
  NB: 15,
  NS: 15,
  PE: 15,
  NL: 15,
  NT: 5,
  NU: 5,
  YT: 5
};


const isSaving = ref(false);
const saveError = ref('');
const saveMessage = ref('');

function createDefaultQuote() {
  const quoteDate = new Date();
  const expiryDate = new Date(quoteDate);
  expiryDate.setDate(expiryDate.getDate() + 30);

  return {
    id: null,
    contractorId: 1,
    contactId: null,
    leadId: null,
    companyId: null,
    quoteNumber: '',
    status: 'draft',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    address: '',
    title: '',
    scope:
      '',
    notes: '',
    discount: 0,
 
    quoteDate: formatDate(quoteDate),
    expiryDate: formatDate(expiryDate),
    paymentInstructions: createDefaultPaymentInstructions(),
    
    jobCity: '',
    jobPostalCode: '',
    jobAddress1: '',
    jobAddress2: '',
      jobProvince: 'ON',
    taxRate: 13,
  };
}

function createDefaultPaymentInstructions() {
  return {
    etransfer: {
      enabled: false,
      email: '',
      phone: '',
      recipientName: '',
    },
    cheque: {
      enabled: false,
      payableTo: '',
      instructions: '',
    },
    wire: {
      enabled: false,
      bankName: '',
      institutionNumber: '',
      transitNumber: '',
      accountNumber: '',
      accountHolder: '',
      notes: '',
    },
  };
}

function createDefaultItems() {
  return [

  /*
    {
      id: 1,
      title: 'Pot Light Installation',
      description: 'Includes wiring and install',
      qty: 8,
      price: 85,
    },
    {
      id: 2,
      title: 'Electrical Outlets',
      description: 'Install 4 new outlets',
      qty: 4,
      price: 55,
    },*/
  ];
}

const quote = ref(createDefaultQuote());
watch(
  () => quote.value.jobProvince,
  (province) => {
    if (taxRates[province] != null) {
      quote.value.taxRate = taxRates[province];
    }
  }
);
const items = ref(createDefaultItems());
const availableQuotes = ref([]);
const selectedQuoteId = ref(null);
const isLoadingQuotes = ref(false);
const quotesError = ref('');

const subtotal = computed(() =>
  items.value.reduce((sum, item) => sum + item.qty * item.price, 0)
);

const tax = computed(() =>
  ((subtotal.value - quote.value.discount) * quote.value.taxRate) / 100
);

const total = computed(() =>
  subtotal.value - quote.value.discount + tax.value
);

const quoteOptions = computed(() =>
  availableQuotes.value.map((quoteRow) => {
    const quoteNumber = quoteRow.quote_number || `Quote #${quoteRow.id}`;
    const customer = quoteRow.customer_name || 'Unnamed client';
    const title = quoteRow.title ? ` - ${quoteRow.title}` : '';
    const status = quoteRow.status ? ` (${quoteRow.status})` : '';

    return {
      ...quoteRow,
      label: `${quoteNumber} - ${customer}${title}${status}`,
    };
  })
);

const previewHref = computed(() => {
  if (quote.value.id) {
    return `/quote-preview?id=${encodeURIComponent(quote.value.id)}`;
  }

  const quoteNumber = quote.value.quoteNumber?.trim();

  if (quoteNumber) {
    return `/quote-preview/${encodeURIComponent(quoteNumber)}`;
  }

  return '';
});

function formatMoney(value) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(value || 0);
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function roundCurrency(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function normalizeItemForPayload(item, index) {
  const quantity = Number(item.qty) || 0;
  const unitPrice = roundCurrency(item.price);

  return {
    id: item.id,
    item_order: index + 1,
    item_name: item.title || '',
    description: item.description || '',
    quantity,
    unit_price: unitPrice,
    total_price: roundCurrency(quantity * unitPrice),
  };
}

function normalizeStoredItems(value) {
  if (!value) {
    return [];
  }

  let lineItems = value;

  if (typeof value === 'string') {
    try {
      lineItems = JSON.parse(value || '[]');
    } catch {
      return [];
    }
  }

  if (!Array.isArray(lineItems)) {
    return [];
  }

  return lineItems.map((item, index) => ({
    id: item.id || `stored-${index}`,
    title: item.item_name || item.title || item.name || '',
    description: item.description || '',
    qty: Number(item.quantity ?? item.qty ?? 1),
    price: Number(item.unit_price ?? item.price ?? 0),
  }));
}

function normalizeStoredPaymentInstructions(value) {
  if (!value) {
    return createDefaultPaymentInstructions();
  }

  let storedInstructions = value;

  if (typeof value === 'string') {
    try {
      storedInstructions = JSON.parse(value || '{}');
    } catch {
      return createDefaultPaymentInstructions();
    }
  }

  const defaults = createDefaultPaymentInstructions();

  return {
    etransfer: {
      ...defaults.etransfer,
      ...storedInstructions.etransfer,
      recipientName:
        storedInstructions.etransfer?.recipientName ||
        storedInstructions.etransfer?.recipient_name ||
        '',
    },
    cheque: {
      ...defaults.cheque,
      ...storedInstructions.cheque,
      payableTo:
        storedInstructions.cheque?.payableTo ||
        storedInstructions.cheque?.payable_to ||
        '',
    },
    wire: {
      ...defaults.wire,
      ...storedInstructions.wire,
      bankName:
        storedInstructions.wire?.bankName ||
        storedInstructions.wire?.bank_name ||
        '',
      institutionNumber:
        storedInstructions.wire?.institutionNumber ||
        storedInstructions.wire?.institution_number ||
        '',
      transitNumber:
        storedInstructions.wire?.transitNumber ||
        storedInstructions.wire?.transit_number ||
        '',
      accountNumber:
        storedInstructions.wire?.accountNumber ||
        storedInstructions.wire?.account_number ||
        '',
      accountHolder:
        storedInstructions.wire?.accountHolder ||
        storedInstructions.wire?.account_holder ||
        '',
    },
  };
}

function normalizePaymentInstructionsForPayload(instructions) {
  return {
    etransfer: {
      enabled: Boolean(instructions.etransfer.enabled),
      email: instructions.etransfer.email || '',
      phone: instructions.etransfer.phone || '',
      recipient_name: instructions.etransfer.recipientName || '',
    },
    cheque: {
      enabled: Boolean(instructions.cheque.enabled),
      payable_to: instructions.cheque.payableTo || '',
      instructions: instructions.cheque.instructions || '',
    },
    wire: {
      enabled: Boolean(instructions.wire.enabled),
      bank_name: instructions.wire.bankName || '',
      institution_number: instructions.wire.institutionNumber || '',
      transit_number: instructions.wire.transitNumber || '',
      account_number: instructions.wire.accountNumber || '',
      account_holder: instructions.wire.accountHolder || '',
      notes: instructions.wire.notes || '',
    },
  };
}

function toInputDate(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  if (typeof value === 'string') {
    return value.slice(0, 10);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? fallback : formatDate(date);
}

function normalizeQuoteRow(row) {
  return {
    ...row,
    id: row?.id ? Number(row.id) : null,
    contractor_id: row?.contractor_id ? Number(row.contractor_id) : 1,
    discount: Number(row?.discount || 0),
    tax_rate: row?.tax_rate == null ? null : Number(row.tax_rate),
    subtotal: Number(row?.subtotal || 0),
    tax_amount: Number(row?.tax_amount || 0),
    total_amount: Number(row?.total_amount || 0),
  };
}

function resolveTaxRate(loadedQuote, defaultTaxRate) {
  if (Number.isFinite(loadedQuote.tax_rate)) {
    return loadedQuote.tax_rate;
  }

  const taxableSubtotal = loadedQuote.subtotal - loadedQuote.discount;

  if (taxableSubtotal) {
    return roundCurrency((loadedQuote.tax_amount / taxableSubtotal) * 100);
  }

  return defaultTaxRate;
}

function applyQuoteRow(row) {
  if (!row?.id) {
    return;
  }

  const loadedQuote = normalizeQuoteRow(row);
  const defaultQuote = createDefaultQuote();
  const taxRate = resolveTaxRate(loadedQuote, defaultQuote.taxRate);
  const loadedItems = normalizeStoredItems(
    loadedQuote.line_items || loadedQuote.items || loadedQuote.quote_items
  );

  selectedQuoteId.value = loadedQuote.id;
  quote.value = {
    ...defaultQuote,
    id: loadedQuote.id,
    contractorId: loadedQuote.contractor_id || defaultQuote.contractorId,
    contactId: loadedQuote.contact_id || defaultQuote.contactId,
    leadId: loadedQuote.lead_id || defaultQuote.leadId,
    companyId: loadedQuote.company_id || defaultQuote.companyId,
    quoteNumber: loadedQuote.quote_number || defaultQuote.quoteNumber,
    status: loadedQuote.status || defaultQuote.status,
    clientName: loadedQuote.customer_name || '',
    clientEmail: loadedQuote.customer_email || '',
    clientPhone: loadedQuote.customer_phone || '',
    address:
      loadedQuote.job_address ||
      loadedQuote.customer_address ||
      loadedQuote.address ||
      '',
    title: loadedQuote.title || '',
    scope: loadedQuote.description || '',
    notes: loadedQuote.notes || '',
    discount: Number(loadedQuote.discount || 0),
    taxRate,
    quoteDate: toInputDate(loadedQuote.quote_date, defaultQuote.quoteDate),
    expiryDate: toInputDate(loadedQuote.expiry_date, defaultQuote.expiryDate),
    paymentInstructions: normalizeStoredPaymentInstructions(
      loadedQuote.payment_instructions
    ),
  };

  items.value = loadedItems.length
    ? loadedItems
    : [
        {
          id: `quote-${loadedQuote.id}`,
          title:
            loadedQuote.title || loadedQuote.quote_number || 'Quote subtotal',
          description: loadedQuote.description || '',
          qty: 1,
          price: loadedQuote.subtotal,
        },
      ];

  saveError.value = '';
  saveMessage.value = `${quote.value.quoteNumber} loaded.`;
}

function selectQuote(quoteId) {
  if (!quoteId) {
    selectedQuoteId.value = null;
    return;
  }

  const quoteRow = availableQuotes.value.find(
    (row) => String(row.id) === String(quoteId)
  );

  if (quoteRow) {
    applyQuoteRow(quoteRow);
  }
}

function startNewQuote() {
  selectedQuoteId.value = null;
  quote.value = createDefaultQuote();
  items.value = createDefaultItems();
  saveError.value = '';
  saveMessage.value = '';
}

function upsertAvailableQuote(row) {
  if (!row?.id) {
    return;
  }

  const normalized = normalizeQuoteRow(row);
  const index = availableQuotes.value.findIndex(
    (existing) => String(existing.id) === String(normalized.id)
  );

  if (index >= 0) {
    availableQuotes.value.splice(index, 1, normalized);
  } else {
    availableQuotes.value.unshift(normalized);
  }

  selectedQuoteId.value = normalized.id;
}

async function loadQuotes() {
  isLoadingQuotes.value = true;
  quotesError.value = '';

  // No query params needed — the backend scopes results to the actor's own
  // company (or all companies for an admin) from the Bearer token alone.
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/invoice-quotes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.message || data?.error || 'Unable to load quotes.'
      );
    }

    const rows = Array.isArray(data) ? data : data?.quotes || data?.data || [];
    availableQuotes.value = rows.map(normalizeQuoteRow);
  } catch (error) {
    quotesError.value = error.message || 'Unable to load quotes.';
    console.error('Load quotes failed:', error);
  } finally {
    isLoadingQuotes.value = false;
  }
}

function addItem() {
  items.value.push({
    id: Date.now(),
    title: '',
    description: '',
    qty: 1,
    price: 0,
  });
}

function removeItem(id) {
  items.value = items.value.filter((item) => item.id !== id);
}

async function saveQuote() {
  isSaving.value = true;
  saveError.value = '';
  saveMessage.value = '';

  const payload = {
    ...(quote.value.id ? { id: quote.value.id } : {}),
    ...(quote.value.contactId ? { contact_id: quote.value.contactId } : {}),
    ...(quote.value.leadId ? { lead_id: quote.value.leadId } : {}),
    ...(quote.value.companyId ? { company_id: quote.value.companyId } : {}),
    quote_number: quote.value.quoteNumber,
    status: quote.value.status,
    customer_name: quote.value.clientName,
    customer_phone: quote.value.clientPhone,
    customer_email: quote.value.clientEmail,
    job_address: quote.value.address,
    title: quote.value.title,
    description: quote.value.scope,
    notes: quote.value.notes,
    discount: roundCurrency(quote.value.discount),
    tax_rate: Number(quote.value.taxRate) || 0,
    subtotal: roundCurrency(subtotal.value),
    tax_amount: roundCurrency(tax.value),
    total_amount: roundCurrency(total.value),
    quote_date: quote.value.quoteDate,
    expiry_date: quote.value.expiryDate,
    line_items: items.value.map(normalizeItemForPayload),
    payment_instructions: normalizePaymentInstructionsForPayload(
      quote.value.paymentInstructions
    ),
  };

  console.log('Save draft payload:', payload);

  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/invoice-quotes/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const savedQuote = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        savedQuote?.message || savedQuote?.error || 'Unable to save quote.'
      );
    }

    const savedId =
      savedQuote?.id || savedQuote?.quote?.id || savedQuote?.data?.id;

    if (savedId) {
      quote.value.id = savedId;
    }

    upsertAvailableQuote(savedQuote?.quote || savedQuote?.data || savedQuote);

    getAuthToken().then(token => logAction(token, {
      action: 'quote.save',
      label: `Saved quote ${quote.value.quoteNumber}`,
      company_id: company.company_id,
    })).catch(() => {});

    saveMessage.value = 'Quote saved.';

    return savedQuote;
  } catch (error) {
    saveError.value = error.message || 'Unable to save quote.';
    console.error('Save quote failed:', error);

    return null;
  } finally {
    isSaving.value = false;
  }
}

// Falls back to the signed-in contractor's own company whenever nothing more
// specific (a lead link's ?company_id=, or an already-loaded quote) has set
// one. `company.company_id` can still be empty at mount if appConfig's auth
// flow hasn't resolved it yet, so this stays reactive rather than a one-shot
// read — it backfills quote.value.companyId the moment it does arrive.
watchEffect(() => {
  if (!quote.value.companyId && company.company_id) {
    quote.value.companyId = company.company_id;
  }
});

onMounted(() => {
  loadQuotes();

  const { contact_id, lead_id, company_id, customer_name, customer_email, customer_phone } = route.query;

  if (contact_id) {
    quote.value.contactId = String(contact_id);
    quote.value.leadId = lead_id ? String(lead_id) : quote.value.leadId;
    quote.value.companyId = company_id ? String(company_id) : quote.value.companyId;
    quote.value.clientName = customer_name ? String(customer_name) : quote.value.clientName;
    quote.value.clientEmail = customer_email ? String(customer_email) : quote.value.clientEmail;
    quote.value.clientPhone = customer_phone ? String(customer_phone) : quote.value.clientPhone;
  }
});
</script>












<template>
  <div class="quote-page" style="">
    <header class="topbar">



      <!--
      <div class="topbar-actions">
        <v-btn variant="outlined">
          <v-icon start>mdi-file-eye-outline</v-icon>
          Preview
        </v-btn>

        <v-btn class="send-btn">
          <v-icon start>mdi-send-outline</v-icon>
          Send Quote
        </v-btn>
      </div>-->

    <div class="bottom-actions">
      <p v-if="saveError" class="save-status save-status--error">
        {{ saveError }}
      </p>

      <p v-else-if="saveMessage" class="save-status">
        {{ saveMessage }}
      </p>

      <v-btn
        variant="outlined"
        size="large"
        :href="previewHref || undefined"
        target="_blank"
        rel="noopener"
        :disabled="!previewHref"
      >
        <v-icon start>mdi-file-eye-outline</v-icon>
        Preview
      </v-btn>

      <v-btn
        variant="outlined"
        size="large"
        :loading="isSaving"
        :disabled="isSaving"
        @click="saveQuote"
      >
        Save Quote
      </v-btn>

      <v-btn class="send-btn" size="large">
        <v-icon start>mdi-send-outline</v-icon>
        Send Quote
      </v-btn>
    </div>

    </header>

    <section class="card quote-picker-card">
      <div class="section-head quote-picker-head">
        <div>
          <h2>Available Quotes</h2>
          <p>Select an existing quote from the quotes table.</p>
        </div>

        <v-btn
          elevation="0"
          size="large"
          :loading="isLoadingQuotes"
          @click="loadQuotes"
        >
          <v-icon  size="25">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div class="quote-picker-row">
        <v-select
          v-model="selectedQuoteId"
          :items="quoteOptions"
          item-title="label"
          item-value="id"
          label="Select Quote"
          variant="outlined"
          clearable
          no-data-text="No quotes found"
          :loading="isLoadingQuotes"
          :disabled="isLoadingQuotes && !quoteOptions.length"
          :error-messages="quotesError ? [quotesError] : []"
          hide-details="auto"
          @update:model-value="selectQuote"
        />

        <v-btn height="55px" variant="outlined" @click="startNewQuote">
          <v-icon start>mdi-plus</v-icon>
          New Quote
        </v-btn>
      </div>
    </section>





    <section class="card">
      <div class="section-head">
        <div>
          <h2>Client & Job</h2>
          <p>Basic customer and project details.</p>
        </div>
      </div>


      <div class="quote-picker-row">
        <v-select
          v-model="selectedCustomerId"
          :items="customerOptions"
          item-title="label"
          item-value="id"
          label="Select Customer"
          variant="outlined"
          clearable
          no-data-text="No customers found"
          :loading="isLoadingQuotes"
          :disabled="isLoadingQuotes && !quoteOptions.length"
          :error-messages="quotesError ? [quotesError] : []"
          hide-details="auto"
          @update:model-value="selectQuote"
        />

        <v-btn height="55px" variant="outlined" @click="startNewQuote">
          <v-icon start>mdi-plus</v-icon>
          New Customer
        </v-btn>
      </div>


      <div class="form-grid mt-3">
        <v-text-field
          v-model="quote.clientName"
          label="Client Name"
          variant="outlined"
          hide-details
        />

        <v-text-field
          v-model="quote.clientPhone"
          label="Phone"
          variant="outlined"
          hide-details
        />

        <v-text-field
          v-model="quote.clientEmail"
          label="Email"
          variant="outlined"
          hide-details
        />

     


 
  <v-text-field
    v-model="quote.jobAddress1"
    label="Street Address"
    variant="outlined"
    hide-details
  />

  <v-text-field
    v-model="quote.jobAddress2"
    label="Unit / Suite"
    variant="outlined"
    hide-details
  />

  <v-text-field
    v-model="quote.jobCity"
    label="City"
    variant="outlined"
    hide-details
  />

  <v-select
    v-model="quote.jobProvince"
    :items="[
      'AB',
      'BC',
      'MB',
      'NB',
      'NL',
      'NS',
      'NT',
      'NU',
      'ON',
      'PE',
      'QC',
      'SK',
      'YT'
    ]"
    label="Province"
    variant="outlined"
    hide-details
  />

  <v-text-field
    v-model="quote.jobPostalCode"
    label="Postal Code"
    variant="outlined"
    hide-details
  />
 
 <v-text-field
  v-model="quote.taxRate"
  label="Tax Rate (%)"
  type="number"
    variant="outlined"
    hide-details
/>
      </div>

      <div class="form-grid mt-3">
     
        <v-btn height="55px" variant="outlined" >
          <v-icon start>mdi-plus</v-icon>
          Add Customer to CRM
        </v-btn>
      </div>
    </section>

    <section class="card">
      <div class="section-head">
        <div>
          <h2>Scope of Work</h2>
          <p>Describe the work being completed.</p>
        </div>


        <v-btn elevation="0" size="large">
 
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="31" height="31" fill="currentColor">
              <!-- Main Large Star -->
              <path d="M12 3C12 7.42 15.58 11 20 11C15.58 11 12 14.58 12 19C12 14.58 8.42 11 4 11C8.42 11 12 7.42 12 3Z" />
              <!-- Top Left Small Star -->
              <path d="M5 3C5 4.66 6.34 6 8 6C6.34 6 5 7.34 5 9C5 7.34 3.66 6 2 6C3.66 6 5 4.66 5 3Z" />
              <!-- Bottom Right Small Star -->
              <path d="M19 15C19 16.1 19.9 17 21 17C19.9 17 19 17.9 19 19C19 17.9 18.1 17 17 17C18.1 17 19 16.1 19 15Z" />
            </svg>
        </v-btn>
      </div>

      <v-text-field
          v-model="quote.title"
          label="Project Title"
          variant="outlined"
          hide-details
      />
        
      <v-textarea
        class="mt-3"
        v-model="quote.scope"
        label="Project Description"
        variant="outlined"
        rows="5"
        auto-grow
        hide-details
      />
    </section>

    <section class="card">
      <div class="section-head">
        <div>
          <h2>Line Items</h2>
          <p>Add labor, materials, and services.</p>
        </div>

        <v-btn variant="outlined" @click="addItem">
          <v-icon start>mdi-plus</v-icon>
          Add Item
        </v-btn>
      </div>

      <div class="items-list">
        <div v-for="item in items" :key="item.id" class="item-card">
          <div class="item-top">
            <strong>Line Item</strong>

            <v-btn
              icon
              variant="text"
              size="small"
              @click="removeItem(item.id)"
            >
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>

          <v-text-field
            v-model="item.title"
            label="Item Name"
            variant="outlined"
            hide-details
          />

          <v-textarea
            v-model="item.description"
            label="Description"
            variant="outlined"
            rows="2"
            auto-grow
            hide-details
            class="mt-3"
          />

          <div class="item-fields">
            <v-text-field
              v-model.number="item.qty"
              label="Qty"
              type="number"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model.number="item.price"
              label="Unit Price"
              prefix="$"
              type="number"
              variant="outlined"
              hide-details
            />
          </div>

          <div class="item-total">
            <span>Line Total</span>
            <strong>{{ formatMoney(item.qty * item.price) }}</strong>
          </div>
        </div>
      </div>
    </section>

    <section class="card totals-card">
      <div class="section-head">
        <div>
          <h2>Totals</h2>
          <p>Quote pricing summary.</p>
        </div>
      </div>

      <div class="totals-list">
        <div>
          <span>Subtotal</span>
          <strong>{{ formatMoney(subtotal) }}</strong>
        </div>

        <div>
          <span>Discount</span>
          <strong>-{{ formatMoney(quote.discount) }}</strong>
        </div>

        <div>
          <span>Tax ({{ quote.taxRate }}%)</span>
          <strong>{{ formatMoney(tax) }}</strong>
        </div>

        <div class="grand-total">
          <span>Total</span>
          <strong>{{ formatMoney(total) }}</strong>
        </div>
      </div>
    </section>

    <section class="card">
      <div class="section-head">
        <div>
          <h2>Payment Instructions</h2>
          <p>Shown to the client after they accept the quote.</p>
        </div>
      </div>

      <div class="payment-methods">
        <div class="payment-method-card">
          <v-checkbox
            v-model="quote.paymentInstructions.etransfer.enabled"
            label="Accept e-transfer"
            hide-details
          />

          <div class="form-grid">
            <v-text-field
              v-model="quote.paymentInstructions.etransfer.email"
              label="E-transfer Email"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.etransfer.phone"
              label="E-transfer Phone"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.etransfer.recipientName"
              label="Recipient Name"
              variant="outlined"
              hide-details
            />
          </div>
        </div>

        <div class="payment-method-card">
          <v-checkbox
            v-model="quote.paymentInstructions.cheque.enabled"
            label="Accept cheque"
            hide-details
          />

          <div class="form-grid">
            <v-text-field
              v-model="quote.paymentInstructions.cheque.payableTo"
              label="Cheque Payable To"
              variant="outlined"
              hide-details
            />

            <v-textarea
              v-model="quote.paymentInstructions.cheque.instructions"
              label="Cheque Instructions"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details
            />
          </div>
        </div>

        <div class="payment-method-card">
          <v-checkbox
            v-model="quote.paymentInstructions.wire.enabled"
            label="Accept wire transfer"
            hide-details
          />

          <div class="form-grid">
            <v-text-field
              v-model="quote.paymentInstructions.wire.bankName"
              label="Bank Name"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.wire.accountHolder"
              label="Account Holder"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.wire.institutionNumber"
              label="Institution Number"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.wire.transitNumber"
              label="Transit Number"
              variant="outlined"
              hide-details
            />

            <v-text-field
              v-model="quote.paymentInstructions.wire.accountNumber"
              label="Account Number"
              variant="outlined"
              hide-details
            />

            <v-textarea
              v-model="quote.paymentInstructions.wire.notes"
              label="Wire Notes"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details
            />
          </div>
        </div>
      </div>
    </section>

    <section class="card">
      <div class="section-head">
        <div>
          <h2>Internal Notes</h2>
          <p>Only visible to your team.</p>
        </div>
      </div>

      <v-textarea
        v-model="quote.notes"
        placeholder="Optional notes"
        variant="outlined"
        rows="3"
        auto-grow
        hide-details
      />
    </section>
<!--
    <div class="bottom-actions">
      <p v-if="saveError" class="save-status save-status--error">
        {{ saveError }}
      </p>

      <p v-else-if="saveMessage" class="save-status">
        {{ saveMessage }}
      </p>

      <v-btn
        variant="outlined"
        size="large"
        :loading="isSaving"
        :disabled="isSaving"
        @click="saveQuote"
      >
        Save Quote
      </v-btn>

      <v-btn class="send-btn" size="large">
        <v-icon start>mdi-send-outline</v-icon>
        Send Quote
      </v-btn>
    </div>-->
  </div>
</template>

<style scoped>
.quote-page {
  width: min(1000px, 100%);
  margin: 0 auto;
  padding: 24px 16px 120px;

  color: var(--text-primary);
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 24px;
}

.topbar h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
}

.topbar p,
.section-head p {
  margin-top: 6px;
  color: var(--text-secondary);
}

.topbar-actions {
  display: flex;
  gap: 12px;
}

.card {
  margin-bottom: 20px;
  padding: 22px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
 
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.section-head h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.quote-picker-card {
  padding-bottom: 18px;
}

.quote-picker-head {
  align-items: flex-start;
}

.quote-picker-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: start;
}

.items-list {
  display: grid;
  gap: 16px;
}

.item-card {
  padding: 18px;
  background: var(--surface-alt);
  border: 1px solid rgba(23, 35, 31, 0.08);
  border-radius: 14px;
}

.item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.item-fields {
  display: grid;
  grid-template-columns: 120px 180px;
  gap: 14px;
  margin-top: 14px;
}

.item-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(23, 35, 31, 0.08);
}

.item-total span,
.totals-list span {
  color: var(--text-secondary);
}

.payment-methods {
  display: grid;
  gap: 16px;
}

.payment-method-card {
  padding: 16px;
  background: var(--surface-alt);
  border: 1px solid rgba(23, 35, 31, 0.08);
  border-radius: 14px;
}

.payment-method-card :deep(.v-selection-control) {
  min-height: 36px;
  margin-bottom: 14px;
}

.totals-list {
  display: grid;
  gap: 14px;
}

.totals-list > div {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grand-total {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid rgba(23, 35, 31, 0.08);
  font-size: 1.2rem;
}

.send-btn {
  background: #0f9f75;
  color: white;
  font-weight: 800;
  text-transform: none;
}

.bottom-actions {
  position: sticky;
  bottom: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(23, 35, 31, 0.08);
  border-radius: 16px;
  width: 100%
}

.save-status {
  align-self: center;
  margin: 0 auto 0 0;
  color: #0f7a5c;
  font-weight: 700;
}

.save-status--error {
  color: #b42318;
}

@media (max-width: 760px) {
  .topbar,
  .section-head,
  .topbar-actions,
  .bottom-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .form-grid,
  .item-fields,
  .quote-picker-row {
    grid-template-columns: 1fr;
  }

  .quote-page {
    padding: 16px 12px 120px;
  }

  .card {
    padding: 18px;
    border-radius: 14px;
  }
}
</style>
